/**
 * Client service for the public identifier-first email auth endpoints
 * (identify, verification, forgot/reset password).
 * Login lives in AuthContext because it manages the session token.
 */

const API_URL = process.env.REACT_APP_API_URL;

const currentLanguage = (): 'en' | 'ja' =>
  (localStorage.getItem('i18nextLng') || 'en').startsWith('ja') ? 'ja' : 'en';

async function post(path: string, body: Record<string, unknown>): Promise<{ ok: boolean; data: any }> {
  const response = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    // Include cookies so the saillab_ref affiliate cookie reaches verify-email,
    // where signup attribution runs (matches AuthContext's register/login).
    credentials: 'include',
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok, data };
}

export type OAuthProviderId = 'google' | 'microsoft';

export interface IdentifyResult {
  status: 'oauth' | 'password' | 'unverified' | 'none';
  providers?: OAuthProviderId[];
  hasPassword?: boolean;
}

/** Which sign-in method applies to this email (identifier-first login). */
export async function identify(email: string): Promise<IdentifyResult> {
  const { ok, data } = await post('/api/auth/identify', { email });
  if (!ok) throw new Error(data.message || 'Failed to look up account');
  return data as IdentifyResult;
}

/**
 * Consume a verification token; the click proves email ownership, so the
 * server signs the user in. Name + password are then collected as the first
 * onboarding step.
 */
export async function verifyEmail(
  token: string
): Promise<{ ok: boolean; token?: string; onboardingCompleted?: boolean }> {
  const { ok, data } = await post('/api/auth/verify-email', { token });
  return { ok: ok && !!data.token, token: data.token, onboardingCompleted: data.onboardingCompleted };
}

/** Ask for a fresh verification email. Always resolves ok (anti-enumeration). */
export async function resendVerification(email: string): Promise<void> {
  await post('/api/auth/resend-verification', { email, language: currentLanguage() });
}

/** Request a password-reset email. Always resolves ok (anti-enumeration). */
export async function forgotPassword(email: string): Promise<void> {
  await post('/api/auth/forgot-password', { email, language: currentLanguage() });
}

/** Consume a reset token and set the new password. */
export async function resetPassword(token: string, password: string): Promise<{ ok: boolean; code?: string; message?: string }> {
  const { ok, data } = await post('/api/auth/reset-password', { token, password });
  return { ok, code: data.code, message: data.message };
}
